import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SidebarPinnedView} from '@github-ui/repos-issues-sidebar-ui/PinnedViewsNav'
import {RepoSavedViewsNav} from '@github-ui/repos-issues-sidebar-ui/RepoSavedViewsNav'
import {ReposIssuesSidebar} from '@github-ui/repos-issues-sidebar-ui/ReposIssuesSidebar'
import type {SavedViewsResponse} from '@github-ui/saved-views'
import {usePinnedSavedViews} from '@github-ui/saved-views/hooks/use-pinned-saved-views'
import {type ReactNode, useMemo} from 'react'

import type {IssuesLayoutPayload} from './issues-layout-route'

const EMPTY_PINNED_VIEWS: SidebarPinnedView[] = []

export type SidebarOptions = {
  owner: string
  repo: string
  hasProjectsEnabled: boolean
  pinnedViews: SidebarPinnedView[]
  pinnedViewsEnabled: boolean
  pinnedViewsTeamVsPersonalEnabled: boolean
}

export function renderSidebar(options: SidebarOptions) {
  return function IssuesSidebar(collapsible?: boolean): ReactNode {
    const {owner, repo, pinnedViewsEnabled, ...sidebarOptions} = options
    return (
      <ReposIssuesSidebar
        collapsible={collapsible}
        owner={owner}
        repo={repo}
        {...sidebarOptions}
        renderSavedViewsNav={
          pinnedViewsEnabled ? undefined : context => <RepoSavedViewsNav owner={owner} repo={repo} {...context} />
        }
      />
    )
  }
}

// Shared source of truth for the issues sidebar render function so the parent
// (IssuesLayout) and the leaf (Labels) — only one of which consumes it depending
// on the sidebar-layout flag — can't drift apart.
export function useRenderSidebar(
  owner: string,
  repo: string,
  layoutPayload: IssuesLayoutPayload | undefined,
  initialSavedViews?: SavedViewsResponse,
) {
  const hasProjectsEnabled = layoutPayload?.hasProjectsEnabled ?? false
  const initialPinnedViews = layoutPayload?.pinnedViews ?? EMPTY_PINNED_VIEWS
  const pinnedViewsEnabled = layoutPayload?.pinnedViewsEnabled ?? false
  const pinnedViewsTeamVsPersonalEnabled = isFeatureEnabled('issue_pinned_views_team_vs_personal')
  const {pinnedViews: savedViewPins, pinnedCount} = usePinnedSavedViews({
    owner,
    repo,
    enabled: pinnedViewsEnabled,
    initialData: initialSavedViews,
  })
  const pinnedViews = useMemo(
    () =>
      pinnedCount === undefined
        ? initialPinnedViews
        : savedViewPins.map(view => ({
            id: view.id,
            name: view.name,
            icon: view.icon,
            color: view.color,
            visibility: view.visibility,
          })),
    [initialPinnedViews, pinnedCount, savedViewPins],
  )

  return useMemo(() => {
    return renderSidebar({
      owner,
      repo,
      hasProjectsEnabled,
      pinnedViews,
      pinnedViewsEnabled,
      pinnedViewsTeamVsPersonalEnabled,
    })
  }, [owner, repo, hasProjectsEnabled, pinnedViews, pinnedViewsEnabled, pinnedViewsTeamVsPersonalEnabled])
}
